import { useQuery } from '@tanstack/react-query';
import { FileText, Clock, CheckCircle, XCircle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/integrations/supabase/client';

export const ApplicationsWidget = () => {
  const { data: applicationsData, isLoading } = useQuery({
    queryKey: ['applications-widget'],
    queryFn: async () => {
      const { data: applications } = await supabase
        .from('applications')
        .select(`
          id, 
          status, 
          title,
          submitted_at,
          team_id,
          teams(name)
        `)
        .order('submitted_at', { ascending: false })
        .limit(50);

      const draft = applications?.filter(a => a.status === 'draft').length || 0;
      const submitted = applications?.filter(a => a.status === 'submitted').length || 0;
      const approved = applications?.filter(a => a.status === 'approved').length || 0;
      const rejected = applications?.filter(a => a.status === 'rejected').length || 0;

      // Get applications from last 7 days
      const last7Days = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
      const recentApplications = applications?.filter(a => 
        new Date(a.submitted_at) > last7Days
      ).length || 0;

      return {
        total: applications?.length || 0,
        draft,
        submitted,
        approved,
        rejected,
        recent: recentApplications,
        recentApplications: applications?.slice(0, 5) || []
      };
    },
    refetchInterval: 60000,
  });

  if (isLoading) {
    return (
      <Card className="p-6 animate-pulse">
        <div className="h-32 bg-muted rounded"></div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-3 mb-4">
        <FileText className="w-6 h-6 text-blue-500" />
        <h3 className="text-lg font-semibold">Applications</h3>
      </div>
      
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-blue-600">{applicationsData?.submitted || 0}</div>
          <div className="text-sm text-muted-foreground">Submitted</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">{applicationsData?.approved || 0}</div>
          <div className="text-sm text-muted-foreground">Approved</div>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-gray-500" />
            <span className="text-sm">Draft</span>
          </div>
          <Badge variant="outline">{applicationsData?.draft || 0}</Badge>
        </div>
        
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <XCircle className="w-4 h-4 text-red-500" />
            <span className="text-sm">Rejected</span>
          </div>
          <Badge variant="destructive">{applicationsData?.rejected || 0}</Badge>
        </div>

        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-blue-500" />
            <span className="text-sm">Last 7 days</span>
          </div>
          <Badge variant="secondary">{applicationsData?.recent || 0}</Badge>
        </div>
      </div>

      {applicationsData?.recentApplications && applicationsData.recentApplications.length > 0 && (
        <div className="mt-4 pt-4 border-t">
          <h4 className="text-sm font-medium mb-2">Recent Applications</h4>
          <div className="space-y-1 max-h-20 overflow-y-auto">
            {applicationsData.recentApplications.slice(0, 3).map((app) => (
              <div key={app.id} className="text-xs p-2 bg-muted rounded flex justify-between">
                <span className="truncate">{app.title}</span>
                <Badge variant={
                  app.status === 'draft' ? 'outline' :
                  app.status === 'submitted' ? 'default' :
                  app.status === 'approved' ? 'default' : 'destructive'
                }>
                  {app.status}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};